import { createSignal, createResource, createEffect, For, Index, Show } from 'solid-js';
import { formatNumber, parseNumericInput } from '../lib/money.js';
import { me } from '../lib/session.js';

// Variabel Gaji — scheme-level DEFAULT variables. Every employee in the
// scheme inherits these values unless they carry their own override
// (managed on the Karyawan page). var("name") resolves:
// employee override → scheme default.

async function getJSON(url) {
  const res = await fetch(url);
  if (!res.ok) throw new Error('HTTP ' + res.status);
  return res.json();
}

export default function VariabelGaji() {
  const [schemes] = createResource(() => getJSON('/api/schemes'));
  const [scheme, setScheme] = createSignal('');
  createEffect(() => {
    const list = schemes();
    if (list?.length && !scheme()) setScheme(list[0].code);
  });

  const [vars, { refetch }] = createResource(
    scheme,
    (code) => code ? getJSON(`/api/scheme/${code}/vars`) : null,
  );

  const [edit, setEdit] = createSignal(null); // name being edited
  const [adding, setAdding] = createSignal(false);
  const [msg, setMsg] = createSignal(null);

  // ── Saldo (ledger-style variables: Simpanan / Pinjaman) ─────────────────
  const [balances, { refetch: refetchBal }] = createResource(
    scheme,
    (code) => code ? getJSON(`/api/scheme/${code}/balances`) : null,
  );
  const [balRows, setBalRows] = createSignal([]);
  const [balMsg, setBalMsg] = createSignal(null); // own message, shown AT the saldo panel
  createEffect(() => { const l = balances(); if (l) setBalRows(l.map((b) => ({ ...b }))); });
  const addBal = () => setBalRows([...balRows(), { name: '', kind: 'pinjaman' }]);
  const setBalField = (i, k, v) => { const r = balRows().slice(); r[i] = { ...r[i], [k]: v }; setBalRows(r); };
  const removeBal = (i) => { const r = balRows().slice(); r.splice(i, 1); setBalRows(r); };
  async function saveBalances() {
    setBalMsg(null);
    const res = await fetch(`/api/scheme/${scheme()}/balances`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ balances: balRows() }),
    });
    const data = await res.json().catch(() => ({}));
    if (res.ok) { setBalMsg({ ok: true, msg: 'Daftar saldo tersimpan.' }); refetchBal(); }
    else setBalMsg({ ok: false, msg: data.error ?? `HTTP ${res.status}` });
  }

  async function save(name, value, type, choices) {
    setMsg(null);
    const res = await fetch(`/api/scheme/${scheme()}/vars`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ name, value, type, choices }),
    });
    const data = await res.json().catch(() => ({}));
    if (res.ok) {
      setMsg({ ok: true, msg: `${name} tersimpan — berlaku untuk semua karyawan ${scheme()} tanpa override.` });
      setEdit(null);
      setAdding(false);
      refetch();
    } else {
      setMsg({ ok: false, msg: data.error ?? `HTTP ${res.status}` });
    }
  }

  async function saveUnit(name, unit) {
    setMsg(null);
    const res = await fetch(`/api/scheme/${scheme()}/vars/unit`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ name, unit }),
    });
    if (res.ok) refetch();
    else {
      const data = await res.json().catch(() => ({}));
      setMsg({ ok: false, msg: data.error ?? `HTTP ${res.status}` });
    }
  }

  // Superadmin-only: removing a variable also drops its per-employee overrides
  // and can break a scheme document that still references the name.
  async function deleteVar(name) {
    if (!confirm(`Hapus variabel "${name}" dari skema ${scheme()}? Override per-karyawan untuk variabel ini juga ikut terhapus.`)) return;
    setMsg(null);
    const res = await fetch(`/api/scheme/${scheme()}/vars?name=${encodeURIComponent(name)}`, { method: 'DELETE' });
    const data = await res.json().catch(() => ({}));
    if (res.ok) {
      setMsg({ ok: true, msg: `Variabel "${name}" dihapus.` });
      setEdit(null);
      refetch();
    } else setMsg({ ok: false, msg: data.error ?? `HTTP ${res.status}` });
  }

  return (
    <>
      <div class="page-head">
        <h2>Variabel Gaji</h2>
        <div class="spacer" />
        <Show when={schemes()}>
          <div class="seg">
            <For each={schemes()}>{(s) =>
              <button
                class="seg-item"
                classList={{ active: scheme() === s.code }}
                onClick={() => { setScheme(s.code); setMsg(null); setEdit(null); setAdding(false); }}
              >
                {s.code}
              </button>
            }</For>
          </div>
        </Show>
      </div>

      <div class="card card-pad vg-card">
        <p class="ky-vars-note">
          Nilai standar yang dipakai untuk <b>semua karyawan {scheme()}</b>. Jika seorang
          karyawan punya nilai sendiri (diisi di halaman <b>Karyawan</b>), nilai pribadinya
          yang dipakai — mengubah angka di sini tidak memengaruhi karyawan tersebut.
        </p>

        <div class="ky-vars-head">
          <h4 class="kg-hist-title">Default {scheme()}</h4>
          <div class="spacer" />
          <button class="btn ghost" onClick={() => { setAdding(!adding()); setEdit(null); }}>
            {adding() ? 'batal' : '+ variabel'}
          </button>
        </div>

        <Show when={msg()}>
          <div class={`kg-verdict ${msg().ok ? 'ok' : 'fail'}`}>{msg().msg}</div>
        </Show>

        <Show when={adding()}>
          <VarEdit name="" value="" isNew onSave={save} onCancel={() => setAdding(false)} />
        </Show>

        <table class="data">
          <thead>
            <tr><th>Variabel</th><th class="num">Nilai default</th><th>Satuan</th><th class="num">Override</th><th></th></tr>
          </thead>
          <tbody>
            <For each={vars() ?? []}>{(v) =>
              <Show when={edit() !== v.name} fallback={
                <tr><td colspan="5">
                  <VarEdit name={v.name} value={v.value} type={v.type} choices={v.choices} onSave={save} onCancel={() => setEdit(null)} />
                </td></tr>
              }>
                <tr>
                  <td>{v.name}</td>
                  <td class="num">
                    <Show when={v.type === 'enum'} fallback={<span class="money">{formatNumber(v.value)}</span>}>
                      <span class="badge info">{v.value || '—'}</span>
                      <Show when={v.choices?.length}><div class="kg-sub" style="text-align:right">{v.choices.join(' · ')}</div></Show>
                    </Show>
                  </td>
                  <td>
                    <Show when={v.type === 'enum'} fallback={<InlineUnit unit={v.unit ?? ''} onSave={(u) => saveUnit(v.name, u)} />}>
                      <span class="vg-dim">pilihan</span>
                    </Show>
                  </td>
                  <td class="num">
                    <Show when={v.overrides > 0} fallback={<span class="vg-dim">—</span>}>
                      <span class="badge info" title="karyawan dengan nilai sendiri">{v.overrides} karyawan</span>
                    </Show>
                  </td>
                  <td class="num ky-var-actions">
                    <button class="btn ghost" onClick={() => { setEdit(v.name); setAdding(false); }}>ubah</button>
                    <Show when={me()?.role === 'superadmin'}>
                      <button class="btn ghost danger" title="hapus variabel (superadmin)" onClick={() => deleteVar(v.name)}>✕</button>
                    </Show>
                  </td>
                </tr>
              </Show>
            }</For>
          </tbody>
        </table>
        <Show when={vars() && vars().length === 0}>
          <p class="kg-meta-text">Belum ada variabel default untuk skema ini.</p>
        </Show>
      </div>

      {/* ── Saldo (ledger-style variables) ──────────────────────────────── */}
      <div class="card card-pad vg-card">
        <div class="ky-vars-head">
          <h4 class="kg-hist-title">Saldo (Simpanan / Pinjaman) — {scheme()}</h4>
          <div class="spacer" />
          <button class="btn ghost" onClick={addBal}>+ saldo</button>
        </div>
        <p class="ky-vars-note">
          Variabel <b>bersaldo</b> dengan riwayat transaksi. <b>Pinjaman</b> berkurang saat pemrosesan
          gaji memotong angsuran; <b>Simpanan</b> bertambah saat pemrosesan gaji menyetor. Saldo awal &
          koreksi diisi manual di halaman <b>Karyawan</b> (tombol Kelola saldo). Komponen gaji mengikat
          ke saldo lewat klausa <code>= input(angka) saldo "Nama Saldo"</code>; baca saldo berjalan
          dengan <code>saldo("Nama Saldo")</code>.
        </p>
        <table class="data">
          <thead><tr><th>Nama saldo</th><th>Jenis</th><th /></tr></thead>
          <tbody>
            <Index each={balRows()}>{(b, i) =>
              <tr>
                <td><input class="pg-cell" value={b().name} placeholder="Pinjaman Karyawan"
                  onInput={(e) => setBalField(i, 'name', e.currentTarget.value)} /></td>
                <td>
                  <select class="kg-select" value={b().kind} onInput={(e) => setBalField(i, 'kind', e.currentTarget.value)}>
                    <option value="pinjaman">Pinjaman (berkurang saat dipotong)</option>
                    <option value="simpanan">Simpanan (bertambah saat disetor)</option>
                  </select>
                </td>
                <td><button class="btn ghost" title="Hapus saldo" onClick={() => removeBal(i)}>✕</button></td>
              </tr>
            }</Index>
            <Show when={balRows().length === 0}>
              <tr><td colspan="3" class="kg-meta-text">Belum ada saldo untuk skema ini.</td></tr>
            </Show>
          </tbody>
        </table>
        <Show when={balMsg()}>
          <div class={`kg-verdict ${balMsg().ok ? 'ok' : 'fail'}`}>{balMsg().msg}</div>
        </Show>
        <div class="kg-create-actions">
          <div class="spacer" />
          <button class="btn primary" onClick={saveBalances}>Simpan Saldo</button>
        </div>
      </div>
    </>
  );
}

// InlineUnit: a label that becomes a text input on click; Enter saves and
// returns to label form, Escape cancels. Display-only metadata, so the
// interaction stays deliberately lightweight.
function InlineUnit(props) {
  const [editing, setEditing] = createSignal(false);
  const [draft, setDraft] = createSignal('');
  let ref;
  function open() {
    setDraft(props.unit);
    setEditing(true);
    queueMicrotask(() => ref?.focus());
  }
  function commit() {
    // Escape closes the editor first; the blur that follows the unmount
    // must NOT save — hence the guard.
    if (!editing()) return;
    setEditing(false);
    if (draft() !== props.unit) props.onSave(draft());
  }
  return (
    <Show when={editing()} fallback={
      <button class="vg-unit" onClick={open} title="klik untuk mengubah satuan">
        {props.unit || <span class="vg-dim">satuan…</span>}
      </button>
    }>
      <input
        ref={ref}
        type="text" class="vg-unit-input" placeholder="mis. Rp/hari, %"
        value={draft()}
        onInput={(e) => setDraft(e.currentTarget.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') commit();
          if (e.key === 'Escape') setEditing(false); // cancel — commit() guard makes the trailing blur a no-op
        }}
        onBlur={commit}
      />
    </Show>
  );
}

function VarEdit(props) {
  const [name, setName] = createSignal(props.name);
  const [type, setType] = createSignal(props.type || 'number');
  const [value, setValue] = createSignal(props.value);
  const [choicesText, setChoicesText] = createSignal((props.choices || []).join(', '));
  const choices = () => choicesText().split(',').map((s) => s.trim()).filter(Boolean);
  // A numeric value may arrive with grouping (pasted from Excel) → normalise to
  // the engine grammar; enum values are picked from a dropdown, leave untouched.
  const submit = () => props.onSave(name(), type() === 'enum' ? value() : parseNumericInput(value()), type(), choices());

  return (
    <div class="ky-var-edit vg-var-edit">
      <Show when={props.isNew} fallback={<span class="ky-var-name">{props.name}</span>}>
        <input
          type="text" placeholder="Nama variabel (mis. Uang Makan, Golongan)"
          value={name()} onInput={(e) => setName(e.currentTarget.value)}
        />
      </Show>
      <select class="kg-select vg-type" value={type()} onInput={(e) => setType(e.currentTarget.value)}>
        <option value="number">Angka</option>
        <option value="enum">Pilihan</option>
      </select>
      <Show when={type() === 'enum'} fallback={
        <input
          type="text" class="ky-var-value" placeholder="0.00" inputmode="decimal"
          value={value()} onInput={(e) => setValue(e.currentTarget.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') submit(); }}
        />
      }>
        <input
          type="text" class="vg-choices" placeholder="pilihan, pisah koma (mis. A, B, C)"
          value={choicesText()} onInput={(e) => setChoicesText(e.currentTarget.value)}
        />
        <select class="kg-select" value={value()} onInput={(e) => setValue(e.currentTarget.value)}>
          <option value="">default: {choices()[0] || 'pilihan ke-1'}</option>
          <For each={choices()}>{(c) => <option value={c}>{c}</option>}</For>
        </select>
      </Show>
      <button class="btn primary" onClick={submit}>Simpan</button>
      <button class="btn ghost" onClick={props.onCancel}>Batal</button>
    </div>
  );
}
